/**
 * The arsenal: which weapons exist to be OWNED, what slot each fits, and the shape of a
 * player-built loadout.
 *
 * Concept rework 2026-09-07, revised 2026-09-15 (`design/economy/economy-rebuild.md` §3):
 * weapons stopped being merchandise. A player owns weapons and builds their own loadouts:
 *
 *   - Every account starts with the AR, the revolver and the grenade, and one loadout holding
 *     exactly those. Every other weapon is gated by account level ALONE — reaching the level
 *     GRANTS the weapon. There is no purchase step, no price, no "buyable" state.
 *   - A loadout is three slots: a PRIMARY (any long gun), a SIDEARM (a handgun) and the
 *     UTILITY (a thrown item — the frag by default; smoke, flash and incendiary since the third
 *     roster, 2026-09-08). The slot classes are what keep the `WEAPON_SLOT` semantics on the wire honest:
 *     `2` still means "the sidearm", and the weapon state machine's swap timings still mean
 *     what they were tuned to mean.
 *   - Three loadout slots are free. More are bought with coins (premium), up to a cap.
 *   - Each weapon has a few cosmetic recolours (`weapon-skins.ts`) — those still cost coins;
 *     only the weapon itself is now free of a price.
 *
 * Shared because both tiers validate the same thing: the meta tier when a player edits a loadout
 * or a ticket is minted, the realtime tier when a signed ticket arrives. The meta tier is the
 * authority on OWNERSHIP (it derives it from level); the realtime tier only re-checks SHAPE,
 * since a signed ticket already carries the meta tier's word on what the player may carry.
 *
 * `loadout.ts`'s presets stay: bots use them, unsigned dev rooms use them, and an old ticket
 * that carries only a preset id still resolves. Nothing a human equips goes through them now.
 */
import { WEAPON_SLOT } from './protocol.js';
import { AR, GRENADE, REVOLVER, THROWABLES, WEAPONS, weaponById } from './weapons.js';

export type SlotClass = 'primary' | 'sidearm' | 'utility';

/** Which slot a weapon may occupy. Sidearms and the throwables are the exceptions; everything
 * else is a long gun. Declared as the exception list so a new weapon defaults to primary. The
 * utility set is *derived* from `THROWABLES`: a thrown weapon without flight physics cannot be
 * simulated, so it must not be equippable either — one table decides both. */
const SIDEARM_IDS: ReadonlySet<number> = new Set([4, 9, 15, 16, 17]); // Deagle, revolver, snub, SD pistol, machine pistol
const UTILITY_IDS: ReadonlySet<number> = new Set(Object.keys(THROWABLES).map(Number));

export function slotClassOf(weaponId: number): SlotClass {
  if (UTILITY_IDS.has(weaponId)) return 'utility';
  if (SIDEARM_IDS.has(weaponId)) return 'sidearm';
  return 'primary';
}

export const SLOT_CLASS_BY_INDEX: readonly SlotClass[] = ['primary', 'sidearm', 'utility'];

export type WeaponUnlock = { kind: 'default' } | { kind: 'level'; level: number };

/**
 * Level gate per weapon. The three defaults are free. The ladder climbs with the weapon's
 * reach and lethality: the first unlocks (smoke and pistol, level 2) land on a player's second
 * or third match; the anti-materiel rifle (level 18) is the last. Sidearms and throwables share
 * the level of the long gun they pair with: a secondary is a secondary.
 *
 * No price of any kind — reaching the level GRANTS the weapon (`design/economy/economy-rebuild.md`
 * §3, decided 2026-09-15). Levels come from `EconomyService.grantXp`'s curve and a match pays
 * ~100-300 credits worth of XP, so the whole roster opens in roughly forty matches.
 */
export const WEAPON_UNLOCK: Readonly<Record<number, WeaponUnlock>> = {
  [AR.id]: { kind: 'default' },
  [REVOLVER.id]: { kind: 'default' },
  [GRENADE.id]: { kind: 'default' },
  18: { kind: 'level', level: 2 }, // smoke
  4: { kind: 'level', level: 2 }, // pistol
  1: { kind: 'level', level: 3 }, // SMG
  15: { kind: 'level', level: 3 }, // snub
  5: { kind: 'level', level: 4 }, // carbine
  19: { kind: 'level', level: 4 }, // flash
  2: { kind: 'level', level: 5 }, // shotgun
  16: { kind: 'level', level: 5 }, // SD pistol
  10: { kind: 'level', level: 6 }, // PDW
  11: { kind: 'level', level: 7 }, // G3
  20: { kind: 'level', level: 7 }, // incendiary
  17: { kind: 'level', level: 8 }, // machine pistol
  12: { kind: 'level', level: 9 }, // DMR
  3: { kind: 'level', level: 10 }, // sniper
  6: { kind: 'level', level: 12 }, // LMG
  7: { kind: 'level', level: 14 }, // battle rifle
  14: { kind: 'level', level: 16 }, // scout
  13: { kind: 'level', level: 18 }, // anti-materiel
};

export function weaponUnlock(weaponId: number): WeaponUnlock {
  return WEAPON_UNLOCK[weaponId] ?? { kind: 'level', level: 99 };
}

export function isDefaultWeapon(weaponId: number): boolean {
  return weaponUnlock(weaponId).kind === 'default';
}

/** Every weapon id in the catalogue — the arsenal's rows, in id order. */
export const ARSENAL_WEAPON_IDS: readonly number[] = WEAPONS.map((weapon) => weapon.id);

/** A player-built loadout. `slots` is indexed by `WEAPON_SLOT`; `skins` is parallel to it and
 * names a `weapon-skins.ts` id per slot, `0` for stock. */
export interface CustomLoadout {
  name: string;
  slots: readonly [number, number, number];
  skins: readonly [number, number, number];
}

export const LOADOUT_NAME_MAX = 20;
export const LOADOUT_SLOT_COUNT = 3;

/** What every account starts with, and what any unresolvable request falls back to. */
export const DEFAULT_CUSTOM_LOADOUT: CustomLoadout = {
  name: 'Default',
  slots: [AR.id, REVOLVER.id, GRENADE.id],
  skins: [0, 0, 0],
};

/** Loadout slot economy. Three free; each further slot costs coins (premium), to a cap. */
export const FREE_LOADOUT_SLOTS = 3;
export const MAX_LOADOUT_SLOTS = 8;
export const LOADOUT_SLOT_PRICE_COINS = 1500;

export type SlotsVerdict = { ok: true } | { ok: false; reason: string };

/**
 * Shape check on a slot triple: three known weapons, each of the class its slot demands. Says
 * nothing about ownership — that is the meta tier's question and it needs a database to answer.
 */
export function validateLoadoutSlots(slots: unknown): SlotsVerdict {
  if (!Array.isArray(slots) || slots.length !== LOADOUT_SLOT_COUNT) {
    return { ok: false, reason: `a loadout has exactly ${LOADOUT_SLOT_COUNT} slots` };
  }
  for (let index = 0; index < LOADOUT_SLOT_COUNT; index++) {
    const id = slots[index];
    if (typeof id !== 'number' || !Number.isInteger(id) || WEAPONS[id] === undefined) {
      return { ok: false, reason: `slot ${index}: unknown weapon` };
    }
    const wanted = SLOT_CLASS_BY_INDEX[index]!;
    const actual = slotClassOf(id);
    if (actual !== wanted) {
      return { ok: false, reason: `slot ${index}: ${weaponById(id).name} is a ${actual}, the slot takes a ${wanted}` };
    }
  }
  return { ok: true };
}

export function isValidSlotTriple(slots: unknown): slots is [number, number, number] {
  return validateLoadoutSlots(slots).ok;
}

/** A loadout name as stored: trimmed, single-spaced, control characters and markup stripped,
 * cut to `LOADOUT_NAME_MAX`. Empty after cleaning → the caller falls back to a default name. */
export function sanitizeLoadoutName(raw: string): string {
  return raw
    .replace(/[\u0000-\u001f\u007f<>&"'`]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, LOADOUT_NAME_MAX);
}

/** The `weapon-state.ts` `Loadout` a room simulates with, from a slot triple. */
export function loadoutFromSlots(slots: readonly [number, number, number]): { slots: number[] } {
  return { slots: [slots[WEAPON_SLOT.primary], slots[WEAPON_SLOT.sidearm], slots[WEAPON_SLOT.utility]] };
}
